import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, FileCheck2, Gauge, Shield } from 'lucide-react';

interface HealthEvent {
    id: string;
    eventType: 'template_status' | 'template_quality' | 'account_update';
    wabaId: string | null;
    templateId: string | null;
    templateName: string | null;
    language: string | null;
    event: string | null;
    previousQuality: string | null;
    newQuality: string | null;
    reason: string | null;
    createdAt: string;
}

interface Props {
    token: string;
}

const getApiBaseUrl = () => {
    let url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};

const API_BASE_URL = getApiBaseUrl();

const TYPE_META: Record<HealthEvent['eventType'], { label: string; cls: string; Icon: typeof FileCheck2 }> = {
    template_status: { label: 'Template status', cls: 'bg-blue-100 text-blue-700', Icon: FileCheck2 },
    template_quality: { label: 'Quality', cls: 'bg-amber-100 text-amber-700', Icon: Gauge },
    account_update: { label: 'Account', cls: 'bg-purple-100 text-purple-700', Icon: Shield },
};

const EVENT_BADGES: Record<string, string> = {
    APPROVED: 'bg-green-100 text-green-700',
    REJECTED: 'bg-red-100 text-red-700',
    PAUSED: 'bg-amber-100 text-amber-700',
    FLAGGED: 'bg-amber-100 text-amber-700',
    PENDING_DELETION: 'bg-gray-100 text-gray-600',
    VERIFIED_ACCOUNT: 'bg-green-100 text-green-700',
    DISABLED_UPDATE: 'bg-red-100 text-red-700',
    ACCOUNT_RESTRICTION: 'bg-red-100 text-red-700',
    ACCOUNT_VIOLATION: 'bg-red-100 text-red-700',
    ACCOUNT_DELETED: 'bg-red-100 text-red-700',
};

const QUALITY_DOTS: Record<string, string> = {
    GREEN: 'bg-green-500',
    YELLOW: 'bg-amber-500',
    RED: 'bg-red-500',
    UNKNOWN: 'bg-gray-400',
};

export default function MessagingQualityTab({ token }: Props) {
    const [page, setPage] = useState(1);
    const limit = 25;
    const [type, setType] = useState('');
    const [data, setData] = useState<{ data: HealthEvent[]; pagination: any } | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchEvents = useCallback(async (silent = false) => {
        if (!silent) setLoading(true); else setRefreshing(true);
        try {
            const params = new URLSearchParams({ page: String(page), limit: String(limit) });
            if (type) params.set('type', type);
            const res = await fetch(`${API_BASE_URL}/api/saas/messaging-health/events?${params}`, {
                headers: { Authorization: `Bearer ${token}`, 'x-client-type': 'web' }
            });
            const json = await res.json();
            if (json.success) setData(json);
        } catch { /* ignore */ }
        finally { setLoading(false); setRefreshing(false); }
    }, [token, page, type]);

    useEffect(() => { fetchEvents(); }, [fetchEvents]);

    const events = data?.data ?? [];
    const total = data?.pagination?.total || 0;
    const totalPages = data?.pagination?.totalPages || 0;

    const fmt = (d: string) =>
        new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }).format(new Date(d));

    const qualityTransition = (e: HealthEvent) => (
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
            {e.previousQuality && (
                <>
                    <span className={`h-2 w-2 rounded-full ${QUALITY_DOTS[e.previousQuality] || 'bg-gray-400'}`} />
                    {e.previousQuality}
                    <span className="text-gray-400">→</span>
                </>
            )}
            <span className={`h-2 w-2 rounded-full ${QUALITY_DOTS[e.newQuality || ''] || 'bg-gray-400'}`} />
            {e.newQuality || '—'}
        </span>
    );

    if (loading && !data) return (
        <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
    );

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
                <button
                    onClick={() => fetchEvents(true)}
                    disabled={refreshing}
                    className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                    title="Refresh"
                >
                    <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin text-indigo-600' : ''}`} />
                </button>
                <span className="text-sm font-medium text-gray-500">Messaging Quality Events ({total})</span>
                <select
                    value={type}
                    onChange={e => { setType(e.target.value); setPage(1); }}
                    className="ml-auto border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                    <option value="">All event types</option>
                    <option value="template_status">Template status</option>
                    <option value="template_quality">Template quality</option>
                    <option value="account_update">Account updates</option>
                </select>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {['When', 'Type', 'Template', 'Event', 'Reason / Details'].map(h => (
                                    <th key={h} className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {events.map(e => {
                                const meta = TYPE_META[e.eventType];
                                return (
                                    <tr key={e.id} className="hover:bg-indigo-50/30 transition-colors">
                                        <td className="px-5 py-4 whitespace-nowrap text-xs text-gray-600 font-medium">{fmt(e.createdAt)}</td>
                                        <td className="px-5 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}>
                                                <meta.Icon className="h-3 w-3" />
                                                {meta.label}
                                            </span>
                                        </td>
                                        <td className="px-5 py-4 whitespace-nowrap">
                                            {e.templateName
                                                ? (
                                                    <>
                                                        <div className="text-sm text-gray-900">{e.templateName}</div>
                                                        <div className="text-xs text-gray-500">{e.language || ''}</div>
                                                    </>
                                                )
                                                : <span className="text-xs text-gray-400">{e.wabaId ? `WABA ${e.wabaId}` : '—'}</span>}
                                        </td>
                                        <td className="px-5 py-4 whitespace-nowrap">
                                            {e.eventType === 'template_quality'
                                                ? qualityTransition(e)
                                                : (
                                                    <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${EVENT_BADGES[e.event || ''] || 'bg-gray-100 text-gray-600'}`}>
                                                        {(e.event || '—').replace(/_/g, ' ')}
                                                    </span>
                                                )}
                                        </td>
                                        <td className="px-5 py-4 text-xs text-gray-500 max-w-md">
                                            {e.reason
                                                ? <span className={e.event === 'REJECTED' || e.eventType === 'account_update' ? 'text-red-600' : ''}>{e.reason}</span>
                                                : '—'}
                                        </td>
                                    </tr>
                                );
                            })}
                            {events.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-gray-400 text-sm">
                                        No quality events yet. They appear here once Meta sends
                                        template status, quality or account webhooks.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-between bg-white px-4 py-3 rounded-xl border border-gray-200 shadow-sm">
                    <p className="text-sm text-gray-700">
                        Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                    </p>
                    <div className="flex gap-1">
                        <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1} className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">Prev</button>
                        <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages} className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">Next</button>
                    </div>
                </div>
            )}
        </div>
    );
}
